@if (loading()) {
    <app-loader />
} @else if (form) {
    <form class="tw:w-full tw:px-6 tw:py-8 tw:pb-28" [formGroup]="form" (ngSubmit)="onSubmit()">
        <h6 class="tw:mb-6 tw:h6">{{ 'INVOICE_PAYMENT_METHOD.title' | translate }}</h6>

        <div class="tw:mb-6 tw:rounded-lg tw:bg-primary-50 tw:p-4">
            <div class="tw:mb-2 tw:flex tw:justify-between">
                <div class="tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.clientSection' | translate }}</div>
                @if (!clientEditMode() && canEditClientDetails()) {
                    <a class="tw:font-semibold tw:no-underline tw:cursor-pointer" (click)="clientEditMode.set(true)">{{
                        'GENERAL.edit' | translate
                    }}</a>
                }
            </div>
            <div class="tw:flex tw:justify-between tw:gap-2 tw:text-sm">
                @if (clientEditMode()) {
                    <div class="tw:w-1/2">
                        <app-text-input
                            [hasMarginB]="false"
                            formControlName="clientVatNumber"
                            label="{{ 'INVOICE_PAYMENT_METHOD.vat' | translate }}"
                        />
                    </div>
                    <div class="tw:w-1/2">
                        <app-text-input [hasMarginB]="false" formControlName="clientName" label="{{ 'INVOICE_PAYMENT_METHOD.name' | translate }}" />
                    </div>
                } @else {
                    <div class="tw:w-1/2">
                        <div class="tw:font-semibold">{{ 'INVOICE_PAYMENT_METHOD.vat' | translate }}</div>
                        <div class="tw:text-neutral-500">{{ form.value.clientVatNumber || '-' }}</div>
                    </div>
                    <div class="tw:w-1/2">
                        <div class="tw:font-semibold">{{ 'INVOICE_PAYMENT_METHOD.name' | translate }}</div>
                        <div class="tw:text-neutral-500">{{ form.value.clientName || '-' }}</div>
                    </div>
                }
            </div>
            <!-- CAE is the merchant's own activity code, so it stays editable regardless of clientEditMode. -->
            @if (showCaeSelection() && caeOptions().length) {
                <div class="tw:mt-3">
                    <app-dropdown-input-v2 [hasMarginB]="false"
                        formControlName="cae"
                        label="{{ 'INVOICE_PAYMENT_METHOD.cae' | translate }}"
                        textOption="label"
                        [options]="caeOptions()"
                     />
                </div>
            }
        </div>

        @if (hasPaymentAmountToCollect()) {
            <div class="tw:mb-6">
                <app-tab-bar [(activeTab)]="activeTab" [tabOptions]="tabOptions" (tabChanged)="onPaymentTabSelected($event)" />
                <div class="tw:mt-4 tw:text-neutral-500">
                    @if (activeTab() === 'later') {
                        {{ 'INVOICE_PAYMENT_METHOD.payLaterDescription' | translate }}
                    } @else if (activeTab() === 'partial') {
                        {{ 'INVOICE_PAYMENT_METHOD.partialPayDescription' | translate }}
                    } @else if (activeTab() === 'full') {
                        {{ 'INVOICE_PAYMENT_METHOD.fullPayDescription' | translate }}
                    }
                </div>
            </div>
        }

        @if (hasPaymentAmountToCollect() && activeTab() === 'later') {
            <div class="tw:mb-6 tw:rounded-lg tw:border tw:border-primary-100 tw:bg-primary-50 tw:p-4">
                <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.payLaterTitle' | translate }}</div>
                <ul class="tw:list-inside tw:list-disc tw:text-sm">
                    <li>{{ 'INVOICE_PAYMENT_METHOD.payLaterList1' | translate }}</li>
                    <li>{{ 'INVOICE_PAYMENT_METHOD.payLaterList2' | translate }}</li>
                    <!-- <li>{{ "INVOICE_PAYMENT_METHOD.payLaterList3" | translate }}</li> -->
                </ul>
            </div>
        } @else if (hasPaymentAmountToCollect() && activeTab() === 'partial') {
            <div class="tw:mb-6 tw:rounded-lg tw:bg-primary-50 tw:p-4">
                <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.payNow' | translate }}</div>
                <div class="tw:mb-4 tw:flex tw:items-center tw:gap-4">
                    <div class="tw:w-28">
                        <!-- [min]="0" -->
                        <app-text-input
                            formControlName="partialPaymentAmount"
                            [hasMarginB]="false"
                            [submitAttempted]="submitAttempted()"
                            inputType="number"
                            suffix="{{ currentMerchant()?.config?.currency || null }}"
                            placeholder="0.00"
                        />
                    </div>
                    <div class="tw:font-semibold tw:text-neutral-500">
                        {{ 'GENERAL.of' | translate }} {{ maxPaymentAmountCents() / 100 | merchantCurrency }}
                    </div>
                </div>
                <div class="tw:text-neutral-500">
                    {{ 'INVOICE_PAYMENT_METHOD.partialPaymentMissing' | translate }}
                    <span class="tw:font-semibold">{{
                        maxPaymentAmountCents() / 100 - form.value.partialPaymentAmount || 0 | merchantCurrency
                    }}</span>
                </div>
            </div>
        }

        @if (hasPaymentAmountToCollect() && ['full', 'partial'].includes(activeTab())) {
            <div class="tw:mb-6" #paymentMethodsContainer>
                <div class="tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.paymentMethod' | translate }}</div>
                <app-multi-check-buttons errorMessage="{{ 'INVOICE_PAYMENT_METHOD.paymentMethodRequired' | translate }}"
                    [submitAttempted]="submitAttempted()"
                    formControlName="activePaymentMethods"
                    [selectOptions]="paymentMethods()"
                 />
            </div>

            @if (form.get('activePaymentMethods')?.value?.length) {
                <div
                    class="tw:mb-6 tw:rounded-lg tw:bg-primary-50 tw:p-4"
                    [class.tw:!bg-error-100]="submitAttempted() && form.get('salePayments')?.errors"
                >
                    @if (form.get('activePaymentMethods')?.value.length > 1) {
                        <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">
                            {{ 'INVOICE_PAYMENT_METHOD.valueByPaymentMethod' | translate }}
                        </div>
                    } @else {
                        <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.value' | translate }}</div>
                    }
                    <div class="tw:flex tw:flex-col tw:justify-start tw:gap-4" formGroupName="salePayments">
                        @for (line of paymentMethods(); let i = $index; track i) {
                            @if (form.get('activePaymentMethods')?.value.length > 1) {
                                @if (form.get('activePaymentMethods')?.value.includes(line.value)) {
                                    <div class="tw:flex tw:items-center tw:justify-between">
                                        <div class="tw:font-semibold tw:text-neutral-500">{{ line.label }}</div>
                                        <div class="tw:w-28">
                                            <!-- [min]="0" -->
                                            <app-text-input
                                                [hasMarginB]="false"
                                                formControlName="{{ line.value }}"
                                                [submitAttempted]="submitAttempted()"
                                                inputType="number"
                                                suffix="{{ currentMerchant()?.config?.currency || null }}"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                }
                            } @else {
                                @if (form.get('activePaymentMethods')?.value.includes(line.value) && form.value?.salePayments?.[line.value]) {
                                    <div class="tw:flex tw:items-center tw:justify-between tw:font-semibold">
                                        <div class="text-neutral-500">{{ line.label }}</div>
                                        {{ form.value?.salePayments?.[line.value] | merchantCurrency }}
                                    </div>
                                }
                            }
                        }
                    </div>
                    @if (form.get('salePayments')?.errors && submitAttempted()) {
                        <div class="tw:mt-2 tw:text-sm tw:text-error-500">
                            {{ 'INVOICE_PAYMENT_METHOD.paymentSumError' | translate }}
                        </div>
                    }
                </div>
            }
        }

        @if (checkoutMode() === CheckoutMode.INVOICE_REMAINING && existingSaleData()?.invoiceStatus === InvoiceStatus.PARTIALLY_INVOICED) {
            <div class="tw:flex tw:mb-2 tw:items-center tw:justify-start tw:gap-4 tw:px-4 tw:py-2 tw:text-sm tw:bg-primary-100 tw:rounded-lg">
                <app-icon icon="info" class="tw:text-primary-400" />
                {{ 'INVOICE_PREVIEW.cancelOldInvoiceWarning' | translate }}
            </div>
        }

        @if (checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            <app-textarea-input
                [rows]="2"
                formControlName="description"
                invalidMsg="{{ 'INVOICE_PAYMENT_METHOD.descriptionError' | translate }}"
                label="{{ 'INVOICE_PAYMENT_METHOD.descriptionLabel' | translate }}"
                [submitAttempted]="submitAttempted()"
                placeholder="{{ 'INVOICE_PAYMENT_METHOD.descriptionPlaceholder' | translate }}"
            />
        }
        <div class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-10 tw:flex tw:items-center tw:justify-between tw:gap-4 tw:bg-white tw:px-6 tw:pb-4 tw:pt-2 tw:lg:left-1/2">
            @if (checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
                <div class="">
                    <button type="button" class="btn btn-md btn-secondary" [disabled]="saving()" (click)="goBack()">{{ 'GENERAL.back' | translate }}</button>
                </div>
                <div class="tw:flex tw:gap-4">
                    <button type="button" class="btn btn-md btn-secondary" [disabled]="saving()" (click)="saveDraft()">{{ 'GENERAL.saveDraft' | translate }}</button>
                    @if (isChargingMethodSelected()) {
                        <app-submit-btn btnCopy="{{ 'INVOICE_PAYMENT_METHOD.continueToTerminal' | translate }}"
                            [loading]="saving()"
                         />
                    } @else {
                        <app-submit-btn btnCopy="{{ (isInvoicingActive() ? 'INVOICE_PAYMENT_METHOD.doInvoice' : 'INVOICE_PAYMENT_METHOD.doSale') | translate }}"
                            [loading]="saving()"
                         />
                    }
                </div>
            } @else if (checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
                <div class="tw:w-full">
                    <app-submit-btn inputClasses="tw:w-full" btnCopy="{{ 'INVOICE_PAYMENT_METHOD.doInvoice' | translate }}" [loading]="saving()" />
                </div>
            } @else {
                <div class="tw:grid tw:w-full tw:grid-cols-2 tw:gap-4">
                    @if (existingSaleData().status === ISaleStatus.DRAFT) {
                        <button type="button" class="btn btn-md btn-secondary tw:w-full" [disabled]="saving()" (click)="updateDraft()">{{ 'GENERAL.updateDraft' | translate }}</button>
                    }
                    @if (isChargingMethodSelected()) {
                        <app-submit-btn [class.tw:col-span-2]="existingSaleData().status !== ISaleStatus.DRAFT"
                            inputClasses="tw:w-full"
                            btnCopy="{{ 'INVOICE_PAYMENT_METHOD.continueToTerminal' | translate }}"
                            [loading]="saving()"
                         />
                    } @else {
                        <app-submit-btn [class.tw:col-span-2]="existingSaleData().status !== ISaleStatus.DRAFT"
                            inputClasses="tw:w-full"
                            btnCopy="{{ 'GENERAL.pay' | translate }}"
                            [loading]="saving()"
                         />
                    }
                </div>
            }
        </div>
    </form>
}
